import type React from 'react';
import { memo, useMemo, Fragment, useCallback, useContext, useRef, useEffect } from 'react';
import { type Options as MarkdownOptions } from 'react-markdown';
import styled from '@emotion/styled';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import type { Chat } from '@trinity/chat';
import { ChatQueryStatus, ChatResponseType, ReportFormat } from '@trinity/chat';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { ScrollableTable } from '@trinity/chat-components/components/Markdown/ScrollableTable';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { ActiveChatSessionContext } from './context';
import { useDownloadQueryTable } from './hooks/useDownloadQueryTable';
import { BaseDownloadButton } from './Message.Download';
import { ChartResponse } from './Message.Response.Chart';
import { FindQuantAnalysesResponse } from './Message.Response.FindQuantAnalysis';
import { ChartQuantAnalysesResponse } from './Message.Response.QuantAnalysis';
import { FindQuotesResponse } from './Message.Response.Quotes';
import { RoutingResult } from './Message.Response.RoutingResult';
import type { ResponseItem } from './Message.Response.utils';
import { parseResponse } from './Message.Response.utils';

type MessageResponseProps<T extends ChatResponseType = ChatResponseType> = {
  queryIdentifier: string;
  item: Chat.QueryResponse<T> | null;
  state: Chat.QueryState.Value<T>;
  status: ChatQueryStatus;
};

export const MessageResponse = ({ item, state, status, queryIdentifier }: MessageResponseProps) => {
  if (!item) {
    return null;
  }

  switch (item.type) {
    case ChatResponseType.Text:
      return <MessageResponseRawText value={item.value} />;
    case ChatResponseType.Markdown:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={false}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );
    case ChatResponseType.MarkdownHtml:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={true}
          trustedDomains={['*']}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );

    case ChatResponseType.LocationSelection:
      return null;
    case ChatResponseType.Chart:
      return <ChartResponse />;
    case ChatResponseType.BIChartQuantAnalyses:
      return <ChartQuantAnalysesResponse />;
    case ChatResponseType.LocationRouting:
      return (
        <RoutingResult
          item={item}
          state={state as Chat.QueryState.Value<ChatResponseType.LocationRouting>}
          queryIdentifier={queryIdentifier}
        />
      );

    case ChatResponseType.MarketingMessage:
    case ChatResponseType.HcpAvailability:
    case ChatResponseType.HcpSummary:
    case ChatResponseType.HcpSelection:
    case ChatResponseType.SchedulingAssistant:
    case ChatResponseType.SmartRouting:
    case ChatResponseType.FormWorkflow:
    case ChatResponseType.SegmentationTagsNeeded:
    case ChatResponseType.SegmentationQuestionsNeeded:
    case ChatResponseType.SegmentationComplete:
    case ChatResponseType.SegmentationCancelled:
    case ChatResponseType.SegmentChatNeeded:
    case ChatResponseType.SegmentChatCancelled:
    case ChatResponseType.SegmentChatSelected:
      return null; //this is implemented at top level

    // Segmentation builder workflow - handled at top level in Item.Query.tsx
    case ChatResponseType.SegmentationUpload:
    case ChatResponseType.SegmentationConfigure:
    case ChatResponseType.SegmentationEDA:
    case ChatResponseType.SegmentationStrategy:
    case ChatResponseType.SegmentationRefinement:
    case ChatResponseType.SegmentationFeatures:
    case ChatResponseType.SegmentationResults:
    case ChatResponseType.SegmentationExport:
      return null;

    case ChatResponseType.BIFindQuotes:
      return <FindQuotesResponse data={item.data} />;

    case ChatResponseType.BIFindQuantAnalyses:
      return <FindQuantAnalysesResponse data={item.data} />;

    default: {
      const _exhaustive: never = item;
      throw new Error(`Unhandled response type: ${JSON.stringify(_exhaustive)}`);
    }
  }
};

type MarkdownProps = {
  queryIdentifier: string;
  value: string;
  allowHtml?: boolean;
  trustedDomains?: string[];
  showDownloadButton?: boolean;
};

type SectionTag = 'Validated' | 'Synthetic' | 'Low confidence';

type TaggedSection = {
  tag: SectionTag;
  content: string;
  traits: string[];
  preamble?: string;
};

const SECTION_TAG_PATTERN = /\*\*\[(Validated|Synthetic|Low confidence)\]\*\*/g;
const TRAITS_ANNOTATION_PATTERN = /<!--\s*traits:\s*\[[^\]]*\]\s*-->\s*/g;
const TRAITS_USED_PATTERN = /\*\*Traits used:\*\*\s*(.+)/;

const SECTION_STYLES: Record<SectionTag, { bg: string; color: string; label: string; tooltip: string }> = {
  Validated: { bg: '#E8F4FB', color: '#27AAE1', label: 'Validated', tooltip: 'Directly supported by evidence from clinical data, research, or cited sources.' },
  Synthetic: { bg: '#F0E8F8', color: '#7841C9', label: 'Synthetic', tooltip: "Reasoned from this persona's distinctive traits and clinical perspective." },
  'Low confidence': { bg: '#F0F0F0', color: '#7F7F7F', label: 'Limited Evidence Support', tooltip: 'Insufficient evidence or persona basis.' },
};

const ValidatedIcon = () => (
  <svg width="14" height="11" viewBox="0 0 35 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon
      fill="currentColor"
      transform="translate(17.5, 13) rotate(-315) translate(-17.5, -13)"
      points="26 -2.75 26 28.75 9 28.75 9 23.75 21 23.75 21 -2.75"
    />
  </svg>
);

const SyntheticIcon = () => (
  <svg width="22" height="8" viewBox="0 0 54 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <circle cx="27" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <circle cx="47" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <line x1="11" y1="7" x2="22" y2="7" stroke="currentColor" strokeWidth="4" />
    <line x1="31" y1="7" x2="42" y2="7" stroke="currentColor" strokeWidth="4" />
  </svg>
);

const LowConfidenceIcon = () => (
  <svg width="14" height="4" viewBox="0 0 29 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="29" height="6" fill="currentColor" />
  </svg>
);

const SECTION_ICONS: Record<SectionTag, () => React.ReactNode> = {
  Validated: ValidatedIcon,
  Synthetic: SyntheticIcon,
  'Low confidence': LowConfidenceIcon,
};

function parseTaggedSections(value: string): TaggedSection[] | null {
  const tags: { tag: SectionTag; index: number }[] = [];
  let match: RegExpExecArray | null;
  const pattern = new RegExp(SECTION_TAG_PATTERN.source, 'g');

  while ((match = pattern.exec(value)) !== null) {
    tags.push({ tag: match[1] as SectionTag, index: match.index });
  }

  if (tags.length === 0) return null;

  const SECTION_ORDER: Record<SectionTag, number> = {
    Validated: 0,
    Synthetic: 1,
    'Low confidence': 2,
  };

  const preamble = tags[0].index > 0 ? value.slice(0, tags[0].index).trim() : '';

  const sections = tags.map((entry, i) => {
    const contentStart = entry.index + value.slice(entry.index).indexOf('\n') + 1;
    const contentEnd = i < tags.length - 1 ? tags[i + 1].index : value.length;
    let rawContent = value.slice(contentStart, contentEnd).trim();
    rawContent = rawContent.replace(TRAITS_ANNOTATION_PATTERN, '');

    let traits: string[] = [];
    const traitsMatch = rawContent.match(TRAITS_USED_PATTERN);
    if (traitsMatch) {
      traits = traitsMatch[1].split(',').map((t) => t.trim().replace(/_/g, ' ')).filter((t) => t.length > 0);
      rawContent = rawContent.replace(TRAITS_USED_PATTERN, '').trim();
    }

    return {
      tag: entry.tag,
      content: rawContent,
      traits,
      preamble: undefined as string | undefined,
    };
  });

  if (preamble && sections.length > 0) {
    sections[0].preamble = preamble;
  }

  // Only reorder when sections follow the standard assessment pattern
  // (all same-tag sections grouped). Engagement scripts interleave tags
  // intentionally (Synthetic → Validated → Synthetic) — preserve that order.
  // Skip reordering when preamble exists (survey Q labels depend on order).
  if (!preamble) {
    const isAlreadyGrouped = sections.every(
      (s, i) => i === 0 || SECTION_ORDER[s.tag] >= SECTION_ORDER[sections[i - 1].tag]
    );
    if (isAlreadyGrouped) {
      sections.sort((a, b) => SECTION_ORDER[a.tag] - SECTION_ORDER[b.tag]);
    }
  }

  return sections;
}

const SectionChip = styled('span')<{ $bg: string; $color: string }>(({ $bg, $color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '20px',
  backgroundColor: $bg,
  color: $color,
  marginBottom: 4,
  marginTop: 12,
  '&:first-of-type': {
    marginTop: 0,
  },
}));

const TraitBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: '18px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(120, 65, 201, 0.15)' : '#F5F0FB',
  color: theme.palette.mode === 'dark' ? '#B89CE8' : '#7841C9',
  textTransform: 'capitalize',
}));

const TraitsRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginBottom: 6,
});

const Markdown = memo(
  ({ queryIdentifier, value, allowHtml, trustedDomains, showDownloadButton = true }: MarkdownProps) => {
    const session = useContext(ActiveChatSessionContext);
    const features = useContext(GroupFeaturesContext);

    const { download } = useDownloadQueryTable({
      chatInstanceId: session.id,
      queryIdentifier,
    });

    const handleDownload = useCallback(() => {
      download({
        title: `Generating Export`,
        name: `Table Data`,
        extension: ReportFormat.Excel,
      });
    }, [download]);

    const handleDownloadRef = useRef(handleDownload);
    useEffect(() => {
      handleDownloadRef.current = handleDownload;
    }, [handleDownload]);

    const sections = useMemo(() => parseTaggedSections(value), [value]);

    const customMarkdownComponents = useMemo(
      (): MarkdownOptions['components'] => ({
        table: (props) => {
          return (
            <>
              <ScrollableTable>{props.children}</ScrollableTable>
              {features.exportChatData && showDownloadButton && (
                <DownloadButtonWrapper>
                  <BaseDownloadButton label="Download List" onClick={() => handleDownloadRef.current()} />
                </DownloadButtonWrapper>
              )}
            </>
          );
        },
      }),
      [features.exportChatData, showDownloadButton]
    );

    if (sections) {
      return (
        <>
          {sections.map((section, i) => {
            const style = SECTION_STYLES[section.tag];
            return (
              <Fragment key={i}>
                {section.preamble && (
                  <ChatMarkdown
                    components={customMarkdownComponents}
                    value={section.preamble}
                    allowHtml={allowHtml}
                    trustedDomains={trustedDomains}
                  />
                )}
                <Tooltip title={style.tooltip}>
                  <SectionChip $bg={style.bg} $color={style.color}>
                    {SECTION_ICONS[section.tag]()}
                    {style.label}
                  </SectionChip>
                </Tooltip>
                {section.traits.length > 0 && (
                  <TraitsRow>
                    {section.traits.map((trait, j) => (
                      <TraitBadge key={j}>{trait}</TraitBadge>
                    ))}
                  </TraitsRow>
                )}
                <ChatMarkdown
                  components={customMarkdownComponents}
                  value={section.content}
                  allowHtml={allowHtml}
                  trustedDomains={trustedDomains}
                />
              </Fragment>
            );
          })}
        </>
      );
    }

    return (
      <ChatMarkdown
        components={customMarkdownComponents}
        value={value}
        allowHtml={allowHtml}
        trustedDomains={trustedDomains}
      />
    );
  }
);

type MessageResponseRawTextProps = {
  value: string;
};

export const MessageResponseRawText = memo(({ value }: MessageResponseRawTextProps) => {
  const items = useMemo(() => {
    return parseResponse(value);
  }, [value]);

  const renderItem = useCallback((item: ResponseItem, i: number) => {
    switch (item.type) {
      case 'bullets': {
        return (
          <StyledUl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledUl>
        );
      }
      case 'numbered': {
        return (
          <StyledOl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledOl>
        );
      }
      case 'text': {
        if (item.value === '') return <br key={i} />;
        return <div key={i}>{item.value}</div>;
      }
    }
  }, []);

  return <Fragment>{items.map(renderItem)}</Fragment>;
});

const StyledLi = styled('li')({
  marginBottom: 4,

  '&:last-child': {
    marginBottom: 0,
  },
});

const StyledOl = styled('ol')({
  paddingLeft: 20,

  [`& > li`]: {
    paddingLeft: 5,
    marginBottom: 8,
    '&::marker': {
      fontFamily: 'var(--font-semibold)',
    },
  },
});

const StyledUl = styled('ul')({
  paddingInlineStart: 20,
});

const DownloadButtonWrapper = styled.div({
  marginTop: 8,
  marginBottom: 16,
});
